import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth, JUST_AUTHENTICATED_FLAG, api } from '../context/AuthContext';

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? null : 'Enter a valid email.';
}

function validatePasswordStrength(password) {
  if (password.length < 8) return 'Use at least 8 characters.';
  if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) return 'Use a mix of letters and digits.';
  return null;
}

function Hero() {
  return (
    <section className="bg-brand-green text-brand-cream py-16 md:py-20">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
        <span className="text-xs uppercase tracking-[0.2em] text-brand-gold">Get started</span>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl leading-tight">
          Create your LISTING AiGENT account
        </h1>
        <p className="mt-4 text-brand-cream/75 max-w-xl mx-auto">
          Sign up with email and password. Text messaging is optional and can be enabled from Settings any time.
        </p>
      </div>
    </section>
  );
}

export default function Signup() {
  const { toast } = useToast();
  const { setSession } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [ageOk, setAgeOk] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    const errs = {};
    const em = validateEmail(email); if (em) errs.email = em;
    const pw = validatePasswordStrength(password); if (pw) errs.password = pw;
    if (!ageOk) errs.age = 'You must be 18 or older to create an account.';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setBusy(true);
    try {
      const { data } = await api.post('/auth/signup', {
        email: email.trim().toLowerCase(),
        password,
        age_confirmed: true,
      });
      // A brand-new account has never been asked about SMS. We DO want the
      // opt-in modal to appear on this fresh sign-in — allow it.
      sessionStorage.setItem(JUST_AUTHENTICATED_FLAG, '1');
      // Remove the legacy one-shot skip flag if it was ever set.
      sessionStorage.removeItem('la_skip_optin_modal_once');
      setSession(null, data.user);
      toast({ title: 'Welcome!', description: 'Account created. Grab the app to start your 7-day trial.' });
      navigate('/download');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Sign-up failed.';
      toast({ title: 'Sign-up error', description: msg, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
    // Module-level stable imports (`API`, `axios`, `JUST_AUTHENTICATED_FLAG`) intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, password, ageOk, setSession, toast, navigate]);

  return (
    <div className="bg-brand-cream min-h-screen">
      <Hero />
      <section className="py-14">
        <div className="max-w-md mx-auto px-5 sm:px-8">
          <form onSubmit={submit} className="bg-white rounded-2xl border border-brand-cream-dark p-8 md:p-10 space-y-5">
            <div>
              <label htmlFor="su-email" className="block text-sm text-brand-green/70 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-green/50 pointer-events-none" />
                <input id="su-email" type="email" required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 text-brand-green"
                  placeholder="you@brokerage.com" />
              </div>
              {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
            </div>
            <div>
              <label htmlFor="su-password" className="block text-sm text-brand-green/70 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-green/50 pointer-events-none" />
                <input id="su-password" type={showPw ? 'text' : 'password'} required autoComplete="new-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-11 py-3 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 text-brand-green"
                  placeholder="At least 8 characters, mix of letters and digits" />
                <button type="button" onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-green/60 hover:text-brand-green">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password}</p>}
            </div>

            <div>
              <label className="flex items-start gap-2 text-sm text-brand-green cursor-pointer">
                <input type="checkbox" data-testid="signup-age-checkbox" checked={ageOk}
                  onChange={(e) => setAgeOk(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-brand-green" />
                <span>I confirm I am <strong>18 years of age or older</strong>.</span>
              </label>
              {errors.age && <p data-testid="signup-age-error" className="mt-1 text-xs text-red-600">{errors.age}</p>}
            </div>

            <p className="text-xs text-brand-green/70 leading-relaxed">
              Text messaging (SMS) is <strong>optional</strong> and never required to use the app.
              After you sign in you can enable it from Settings; you may opt out at any time.
            </p>

            <button type="submit" disabled={busy} data-testid="signup-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-brand-green text-brand-cream font-semibold hover:scale-[1.01] transition-transform disabled:opacity-70">
              {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Creating…</>) : (<>Create account <ArrowRight className="w-4 h-4" /></>)}
            </button>

            <p className="text-center text-xs text-brand-green/60">
              By creating an account you agree to our{' '}
              <Link to="/terms" className="underline">Terms of Service</Link> and{' '}
              <Link to="/privacy" className="underline">Privacy Policy</Link>.
            </p>
          </form>
          <p className="mt-6 text-center text-sm text-brand-green/70">
            Already have an account?{' '}
            <Link to="/login" className="underline text-brand-green font-semibold">Sign in</Link>
          </p>
        </div>
      </section>
    </div>
  );
}
